// ---------------------------------------------------------------------------
// CSV export helpers
// ---------------------------------------------------------------------------

import {
  GetPaymentAssignmentRequestDto,
  PaymentAssignmentItemDto,
} from 'enrollment-platform/pages/finance/api/dto/get-payment-assignment.dto'
import { normalizeAmountTwoDecimals } from 'helpers/utility'
import { formatDateForCsv, joinNameParts } from './payment-assignment.helper'
import {
  PaymentAssignmentSortBy,
  PaymentFilters,
  PaymentType,
  PersonRelationshipEnum,
  PERSON_RELATIONSHIP_LABELS,
  Priority,
  PRIORITY_LABELS,
  TerminatedFilter,
  TestUserFilter,
} from './types'

interface SortState {
  sortBy: PaymentAssignmentSortBy
  sortOrder: 'asc' | 'desc'
}

export const EXPORT_PAGE_SIZE = 500

/** Maps API booleans or "true"/"false" strings to Yes/No; other strings pass through trimmed. */
function formatDidSetupAutopayForCsv(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') {
    const t = value.trim().toLowerCase()
    if (t === 'true') return 'Yes'
    if (t === 'false') return 'No'
    return value.trim()
  }
  return ''
}

const CSV_HEADERS = [
  'ID',
  'Benefit ID',
  'Payment Period',
  'Employee Name',
  'Insured Person Name',
  'Relation',
  'Company Name',
  'Company Employee ID',
  'Carrier',
  'Group ID',
  'Payment Type',
  'Primary Payment Method',
  'Status',
  'Assignee',
  'Escalator',
  'Payment Submitted By',
  'Expected Cost',
  'Premium Paid',
  'Actual Payment Date',
  'Confirmation Number',
  'Autopay Confirmation',
  'Ticket Number',
  'Priority',
  'Escalation Reason',
  'Binder Payment',
  'Benefit Effective Date',
  'Benefit End Date',
  'Test User',
  'Created At',
  'Updated At',
]

export function escapeCSVCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

export function buildCSV(items: PaymentAssignmentItemDto[]): string {
  const rows = items.map((item) => {
    const period =
      item.year != null && item.month != null
        ? `${item.year}-${String(item.month + 1).padStart(2, '0')}`
        : ''
    const insuredPersonName =
      item.insuredPerson?.firstName != null &&
      item.insuredPerson?.lastName != null
        ? joinNameParts(
            item.insuredPerson.firstName,
            item.insuredPerson.middleName,
            item.insuredPerson.lastName
          )
        : '—'
    const relation = item.insuredPerson?.relationship
      ? (PERSON_RELATIONSHIP_LABELS[
          item.insuredPerson.relationship as PersonRelationshipEnum
        ] ?? item.insuredPerson.relationship)
      : ''
    const priorityLabel =
      item.priority != null
        ? (PRIORITY_LABELS[item.priority as Priority] ?? '')
        : ''
    const binderLabel =
      item.needsBinderPayment === true
        ? 'Needs Binder Payment'
        : item.isBinderPayment === true
          ? 'Binder Payment'
          : ''

    return [
      item.id,
      item.benefitId,
      period,
      item.employeeName,
      insuredPersonName,
      relation,
      item.companyName,
      item.companyEmployeeId,
      item.paymentCarrier?.name ?? item.carrierName,
      item.groupId ?? '',
      item.paymentType,
      item.primaryPaymentMethod ?? '',
      item.paymentStatus?.description ?? item.paymentStatus?.id,
      item.assigneeName,
      item.escalatorName,
      item.paymentSubmittedBy ?? '',
      normalizeAmountTwoDecimals(item.expectedCost),
      item.actualCost,
      formatDateForCsv(item.actualPaymentDate, 'America/Los_Angeles'),
      item.confirmationNumber,
      formatDidSetupAutopayForCsv(item.didSetUpAutopay),
      item.ticket,
      priorityLabel,
      item.escalationReason,
      binderLabel,
      formatDateForCsv(item.benefitEffectiveDate),
      formatDateForCsv(item.benefitEndDate),
      item.isTestUser ? 'Yes' : 'No',
      item.createdAt,
      item.updatedAt,
    ]
      .map(escapeCSVCell)
      .join(',')
  })
  return [CSV_HEADERS.map(escapeCSVCell).join(','), ...rows].join('\n')
}

export function triggerCSVDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const SELECT_ALL_VALUE = '__SELECT_ALL__'

/** Strips nil values and the SELECT_ALL_VALUE sentinel — mirrors sanitizeMulti in usePaymentAssignment. */
function sanitizeMulti(values?: string[]): string[] {
  return (values ?? []).filter((v) => v != null && v !== SELECT_ALL_VALUE)
}

/** Mirrors pendingFilterParams logic in usePaymentAssignment (including sanitizeMulti). */
export function buildExportParams(
  filters: PaymentFilters,
  sort?: SortState | null
): Omit<GetPaymentAssignmentRequestDto, 'limit' | 'page'> {
  const now = new Date()
  const [yearStr, monthStr] = (filters.paymentPeriod ?? '').split('-')
  const year = Number(yearStr) || now.getFullYear()
  const monthFromPicker = Number(monthStr)
  const month =
    Number.isFinite(monthFromPicker) && monthFromPicker > 0
      ? monthFromPicker - 1
      : now.getMonth()

  const carrierNames = (filters.carrierNames ?? []).filter(
    (n) => typeof n === 'string' && n.trim() !== ''
  )
  const paymentTypes = sanitizeMulti(filters.paymentTypes as string[]).map(
    (t) => t as PaymentType
  )
  const priorityLevels =
    Array.isArray(filters.priorityLevels) && filters.priorityLevels.length
      ? filters.priorityLevels
      : undefined
  const statuses = sanitizeMulti(filters.statuses)
  const clientPublicKeys = sanitizeMulti(filters.clientPublicKeys)
  const assigneeIds =
    Array.isArray(filters.assigneeIds) && filters.assigneeIds.length
      ? filters.assigneeIds
      : undefined
  const escalatorIds =
    Array.isArray(filters.escalatorIds) && filters.escalatorIds.length
      ? filters.escalatorIds
      : undefined
  const paymentSubmittedByIds =
    Array.isArray(filters.paymentSubmittedByIds) &&
    filters.paymentSubmittedByIds.length
      ? filters.paymentSubmittedByIds
      : undefined
  const testUserFilter =
    filters.testUserFilter && filters.testUserFilter !== TestUserFilter.ALL
      ? filters.testUserFilter
      : undefined

  const terminatedFilter =
    filters.terminatedFilter &&
    filters.terminatedFilter !== TerminatedFilter.ALL
      ? filters.terminatedFilter
      : undefined

  const relationships =
    Array.isArray(filters.relationships) && filters.relationships.length
      ? filters.relationships
      : undefined

  const benefitEffectiveDateFrom = (
    filters.benefitEffectiveDateFrom ?? ''
  ).trim()
  const benefitEffectiveDateTo = (filters.benefitEffectiveDateTo ?? '').trim()
  const benefitEndDateFrom = (filters.benefitEndDateFrom ?? '').trim()
  const benefitEndDateTo = (filters.benefitEndDateTo ?? '').trim()

  return {
    year,
    month,
    carrierNames: carrierNames.length ? carrierNames : undefined,
    paymentTypes: paymentTypes.length ? paymentTypes : undefined,
    priorityLevels,
    statuses: statuses.length ? statuses : undefined,
    clientPublicKeys: clientPublicKeys.length ? clientPublicKeys : undefined,
    assigneeIds,
    escalatorIds,
    paymentSubmittedByIds,
    search: (filters.search ?? '').trim() || undefined,
    testUserFilter,
    terminatedFilter,
    benefitEffectiveDateFrom: benefitEffectiveDateFrom || undefined,
    benefitEffectiveDateTo: benefitEffectiveDateTo || undefined,
    benefitEndDateFrom: benefitEndDateFrom || undefined,
    benefitEndDateTo: benefitEndDateTo || undefined,
    relationships,
    binderPayment: filters.binderPayment,
    primaryPaymentMethods:
      Array.isArray(filters.primaryPaymentMethods) &&
      filters.primaryPaymentMethods.length
        ? filters.primaryPaymentMethods
        : undefined,
    onlyMigrated: filters.onlyMigrated ? true : undefined,
    ...(sort
      ? {
          sort: sort.sortBy,
          order: (sort.sortOrder === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
        }
      : {}),
  }
}

// ---------------------------------------------------------------------------
